import { db } from "@/services/config";
import {
  feedService,
  blockService,
  FavoritePayload,
  FavoriteRecord,
  VisitorRecord,
} from "@/services";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

export class InterestsService {
  private favoritesRef = collection(db, "favorites");

  private formatUserToPayload(user: any) {
    return {
      username: user?.username || "",
      profileImage: user?.profileImage || "",
      city: user?.city || "",
      state: user?.state || "",
      birthYear: user?.birthYear || "",
      photosCount: user?.publicPhotos?.length || user?.photosCount || 0,
      gender: user?.gender || "",
    };
  }

  async toggleFavorite(
    currentUser: any,
    userData: any,
    target: { id?: string; username?: string; [key: string]: any },
    isSubscriber: boolean,
  ): Promise<boolean> {
    if (!currentUser?.uid || !userData)
      throw new Error("Usuário não autenticado.");

    let targetUid = target.id;
    let targetData = target;

    if (!targetUid && target.username) {
      const profile = await feedService.getProfileByUsername(
        target.username,
        {},
      );
      if (!profile?.id) throw new Error("Usuário alvo não encontrado.");
      targetUid = profile.id;
      targetData = profile;
    }

    if (!targetUid) throw new Error("ID do alvo não definido.");

    const isCurrentlyFavorited = await this.isFavorited(
      currentUser.uid,
      targetUid,
    );

    if (isCurrentlyFavorited) {
      await this.removeFavorite(currentUser.uid, targetUid, isSubscriber);
      return false;
    } else {
      const payload: FavoritePayload = {
        favoritedBy: currentUser.uid,
        targetUser: targetUid,
        targetUserData: this.formatUserToPayload(targetData),
        favoritorData: this.formatUserToPayload(userData),
      };
      await this.favoriteUser(payload, isSubscriber);
      return true;
    }
  }

  async checkIsFavorited(
    myUid: string,
    target: { id?: string; username?: string },
  ): Promise<boolean> {
    if (!myUid) return false;

    let targetUid = target.id;
    if (!targetUid && target.username) {
      const profile = await feedService.getProfileByUsername(
        target.username,
        {},
      );
      targetUid = profile?.id;
    }

    if (!targetUid) return false;
    return this.isFavorited(myUid, targetUid);
  }

  async favoriteUser(
    data: FavoritePayload,
    isSubscriber: boolean,
  ): Promise<FavoriteRecord> {
    if (!isSubscriber) {
      throw new Error("USER_NOT_SUBSCRIBER");
    }

    try {
      const favoriteId = `${data.favoritedBy}_${data.targetUser}`;
      const favDocRef = doc(this.favoritesRef, favoriteId);

      const favoriteData: FavoriteRecord = {
        ...data,
        createdAt: serverTimestamp(),
        id: favoriteId,
      };

      await setDoc(favDocRef, favoriteData);
      return favoriteData;
    } catch (error) {
      console.error("Erro ao salvar favorito:", error);
      throw error;
    }
  }

  async getMyFavorites(uid: string): Promise<FavoriteRecord[]> {
    try {
      const q = query(
        this.favoritesRef,
        where("favoritedBy", "==", uid),
        orderBy("createdAt", "desc"),
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data() as any;
        const targetUserData = data.targetUserData || {};
        const favoritorData = data.favoritorData || {};

        return {
          id: doc.id,
          ...data,
          targetUserData: {
            ...targetUserData,
            photosCount: data.publicPhotos ? data.publicPhotos.length : 0,
          },
          favoritorData: {
            ...favoritorData,
            photosCount: data.publicPhotos ? data.publicPhotos.length : 0,
          },
        };
      }) as FavoriteRecord[];
    } catch (error) {
      console.error("[getDiscoveryFeed] erro:", error);
      throw error;
    }
  }

  async getWhoFavoritedMe(uid: string): Promise<FavoriteRecord[]> {
    const q = query(
      this.favoritesRef,
      where("targetUser", "==", uid),
      orderBy("createdAt", "desc"),
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data() as any;
      const targetUserData = data.targetUserData || {};
      const favoritorData = data.favoritorData || {};

      return {
        id: doc.id,
        ...data,
        targetUserData: {
          ...targetUserData,
          photosCount: data.publicPhotos ? data.publicPhotos.length : 0,
        },
        favoritorData: {
          ...favoritorData,
          photosCount: data.publicPhotos ? data.publicPhotos.length : 0,
        },
      };
    }) as FavoriteRecord[];
  }

  subscribeFavoritesGiven(
    uid: string,
    callback: (data: FavoriteRecord[]) => void,
    onError?: (error: any) => void,
  ) {
    if (!uid) {
      callback([]);
      return () => {};
    }

    const q = query(
      this.favoritesRef,
      where("favoritedBy", "==", uid),
      orderBy("createdAt", "desc"),
    );

    return onSnapshot(
      q,
      (snap) => {
        callback(
          snap.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as FavoriteRecord,
          ),
        );
      },
      onError,
    );
  }

  subscribeFavoritesReceived(
    uid: string,
    callback: (data: FavoriteRecord[]) => void,
    onError?: (error: any) => void,
  ) {
    if (!uid) {
      callback([]);
      return () => {};
    }

    const q = query(
      this.favoritesRef,
      where("targetUser", "==", uid),
      orderBy("createdAt", "desc"),
    );

    return onSnapshot(
      q,
      (snap) => {
        callback(
          snap.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as FavoriteRecord,
          ),
        );
      },
      onError,
    );
  }

  subscribeVisitors(
    uid: string,
    callback: (data: VisitorRecord[]) => void,
    onError?: (error: any) => void,
  ) {
    if (!uid) {
      callback([]);
      return () => {};
    }

    const visitorsRef = collection(db, "visitors");

    const q = query(
      visitorsRef,
      where("targetId", "==", uid),
      orderBy("createdAt", "desc"),
    );

    return onSnapshot(
      q,
      (snap) => {
        callback(
          snap.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as VisitorRecord,
          ),
        );
      },
      onError,
    );
  }

  async isFavorited(myUid: string, targetUid: string): Promise<boolean> {
    const favoriteId = `${myUid}_${targetUid}`;
    const snap = await getDoc(doc(this.favoritesRef, favoriteId));
    return snap.exists();
  }

  async removeFavorite(
    myUid: string,
    targetUid: string,
    isSubscriber: boolean,
  ) {
    if (!isSubscriber) {
      throw new Error("USER_NOT_SUBSCRIBER");
    }

    const favoriteId = `${myUid}_${targetUid}`;
    await deleteDoc(doc(this.favoritesRef, favoriteId));
  }
}

export const interestsService = new InterestsService();
